// @ts-check

/**
 * Animated details element.
 * Requires a transition on element following summary to listen for transitionend
 */
class AnimatedDetails extends HTMLElement {
  constructor() {
    super();
    this.setup();
    this.headerDropDownOpen = new CustomEvent("headerDropDownOpen", { bubbles: true });
  }

  setup() {
    this.detailsEl = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.toggledEl = this.summary.nextElementSibling;
    this.dropDownType = this.getAttribute("data-dropdown");

    this.openState = "open";
    this.closedState = "closed";
    this.openingState = "opening";
    this.closingState = "closing";
    this.isHovered = false;

    this.classes = {
      open: "open",
      closed: "closed",
      beforeClosing: "before-closing",
      beforeOpening: "before-opening",
      closing: "closing",
      opening: "opening",
    };

    this.state = this.detailsEl.hasAttribute("open") ? this.openState : this.closedState;

    this.detailsEl.classList.add(this.classes[this.state]);
    // ensures doesn't close if clicked inside
    this.toggledEl.tabIndex = -1;

    /**
     * when a transition has ended, if this property was what ended then
     * the details is considered "closed" or "open"
     */
    this.transitionedPropertyName = "transform";

    this.bindListeners();
  }

  bindListeners() {
    this.detailsEl.addEventListener("toggle", this.onToggle.bind(this));
    this.summary.addEventListener("click", this.onClick.bind(this));
    this.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.addEventListener("mouseleave", this.onMouseLeave.bind(this));
    this.detailsEl.addEventListener("focusout", this.onFocusOut.bind(this));
    this.toggledEl.addEventListener("transitionend", this.onTransitionEnd.bind(this));
    this.detailsEl.addEventListener("keypress", this.onKeyPress.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
      /**
       * slight delay seems to resolve issues with click
       * not registering when it also triggers a focusout event on FF.
       */
    }, 100);
  }

  onTransitionEnd(e) {
    if (e.propertyName === this.transitionedPropertyName && e.target === this.toggledEl) {
      if (this.state === this.closingState) {
        this.cancelOpening();
        this.cancelClosing();
        this.detailsEl.classList.add(this.classes.closed);
        this.detailsEl.removeAttribute("open");
        this.state = this.closedState;
      } else {
        this.cancelClosing();
        this.cancelOpening();
        this.detailsEl.classList.add(this.classes.open);
        this.state = this.openState;
      }
    }
  }

  cancelOpening() {
    this.detailsEl.classList.remove(this.classes.opening);
    this.detailsEl.classList.remove(this.classes.beforeOpening);
    this.detailsEl.classList.remove(this.classes.open);
  }

  cancelClosing() {
    this.detailsEl.classList.remove(this.classes.closing);
    this.detailsEl.classList.remove(this.classes.beforeClosing);
    this.detailsEl.classList.remove(this.classes.closed);
  }

  onMouseEnter() {
    this.open();
    this.isHovered = true;
  }

  onMouseLeave() {
    this.close();
    this.isHovered = false;
  }

  onClick(e) {
    let hasTouchScreen = false;
    if ("maxTouchPoints" in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else if ("msMaxTouchPoints" in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else {
      const mediaQuery = matchMedia?.("(pointer:coarse)");
      if (mediaQuery?.media === "(pointer:coarse)") {
        hasTouchScreen = !!mediaQuery.matches;
      } else if ("orientation" in window) {
        // deprecated, but good fallback
        hasTouchScreen = true;
      } else {
        // Only as a last resort, fall back to user agent sniffing
        const UA = navigator.userAgent;
        hasTouchScreen =
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
      }
    }

    // Opens dropdown on click if touchscreen
    if (hasTouchScreen) {
      e.preventDefault();
      this.cancelClosing();
      this.open();
      if (this.state == this.openState) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  close() {
    if (this.state !== this.closedState && this.state !== this.closingState) {
      this.state = this.closingState;
      requestAnimationFrame(() => {
        this.cancelOpening();
        this.detailsEl.classList.add(this.classes.beforeClosing);
        requestAnimationFrame(() => {
          this.detailsEl.classList.add(this.classes.closing);
        });
      });
    }
  }

  open() {
    if (this.state !== this.openingState && this.state !== this.openState) {
      const wasClosing = this.state === this.closingState;
      this.state = this.openingState;
      this.detailsEl.setAttribute("open", "true");
      this.dispatchEvent(this.headerDropDownOpen);

      if (wasClosing) {
        this.cancelClosing();
        this.detailsEl.classList.add(this.classes.opening);
      } else {
        requestAnimationFrame(() => {
          this.cancelClosing();
          this.detailsEl.classList.add(this.classes.beforeOpening);
          requestAnimationFrame(() => {
            this.detailsEl.classList.add(this.classes.opening);
          });
        });
      }
    }
  }

  onToggle() {
    if (this.detailsEl.hasAttribute("open")) {
      this.summary.setAttribute("aria-expanded", "true");
    } else {
      this.summary.setAttribute("aria-expanded", "false");
    }
  }

  onKeyPress(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.open();
    }
  }
}

customElements.define('animated-details', AnimatedDetails)
